/**
 * GoogleDisplayAdMockup Component
 *
 * Google Display Network ad mockup (Responsive Display Ad)
 * Card format showing image, headline, description, and CTA
 */

import React from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';
import { AdMockupContainer } from '../base/AdMockupContainer';
import { AdMockupMedia } from '../base/AdMockupMedia';
import { AdMockupCTA } from '../base/AdMockupCTA';
import { GoogleDisplayAdMockupProps } from '../types/platform.types';

export const GoogleDisplayAdMockup: React.FC<GoogleDisplayAdMockupProps> = ({
  imageUrl,
  headline,
  description,
  callToAction = 'Learn More',
  brandName = 'Your Brand',
}) => {
  return (
    <AdMockupContainer platform="google_display">
      <div className="bg-white dark:bg-card-darkAlt rounded-lg border border-gray-200 dark:border-border-dark overflow-hidden shadow-sm max-w-sm mx-auto">

        {/* Hero Image with Ad Badge */}
        <div className="relative">
          {imageUrl ? (
            <AdMockupMedia
              mediaUrl={imageUrl}
              aspectRatio="16:9"
              platform="google_display"
              alt="Display ad image"
            />
          ) : (
            <div className="aspect-video bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <span className="text-gray-400 dark:text-gray-500 text-sm">
                No image available
              </span>
            </div>
          )}

          {/* Ad badge overlay */}
          <div className="absolute top-2 left-2">
            <span className="px-1.5 py-0.5 bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-600 rounded text-[10px] font-medium text-gray-600 dark:text-gray-300 shadow-sm">
              Ad
            </span>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-3">
          {/* Brand */}
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0">
              {brandName.slice(0, 2).toUpperCase()}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {brandName}
            </span>
          </div>

          {/* Headline */}
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
            {stripMediaTags(headline)}
          </h3>

          {/* Description */}
          {description && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
              {stripMediaTags(description)}
            </p>
          )}

          {/* CTA Button */}
          <AdMockupCTA
            text={callToAction}
            platform="google_display"
            variant="primary"
            fullWidth
          />
        </div>
      </div>
    </AdMockupContainer>
  );
};

export default GoogleDisplayAdMockup;
